import {
	type CaptureFileJSONObject,
	type CollectionJSONObject,
	type CollectionSummaryJSONObject,
	type GeoPointJSON,
	IkeToken,
	ParseJSON,
} from '@ikegps/core-api';
import {
	CollectionModel,
	CollectionModelBase,
	CollectionModelField,
	CollectionModelFieldAnchor,
	CollectionModelFieldFileTool,
	CollectionModelFieldImage,
	CollectionModelFieldText,
	CollectionModelFieldTrueSize,
	CollectionModelFieldVector,
	IkeDrawState,
	IkeId,
} from '@ikegps/core-shared';
import { action, computed, observable, ObservableMap, reaction, runInAction, makeObservable } from 'mobx';
import { TopologicalEditorController } from '.';
import { ModalService } from '../../components/modal/modal.service';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n/index';
import { CollectionChangeState } from '../../modules/collection.view/collection.change.state';
import { MultiListChooserContent, MultiListChooserTitle } from '../../modules/multi.list.chooser/multi.list.chooser';
import { CSS as MultiListChooserCSS } from '../../modules/multi.list.chooser/multi.list.chooser.style';
import { SpanLinkChooserState } from '../../modules/multi.list.chooser/span.link.chooser.state';
import { TopologicalPointCollectionCache } from '../../modules/topological.editor.map/point.collection.cache';
import { Services } from '../../services';
import { type FieldJoin, FieldJoinMatch } from '../../services/field.join/field.join';
import { CollectionEditState } from '../../state/collection.edit.state';
import { MobxState } from '../../state/mobx';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import {
	FileUploadReader,
	ImageUploadReader,
	MAX_UPLOAD_FILE_SIZE_BYTE,
	MAX_UPLOAD_FILE_SIZE_MB,
	validateImageFile,
} from '../../util/upload.reader';
import type { ICollectionViewController } from '../../modules/collection.view/collection.view.ctrl.interface';

export class TopologicalEditorCollectionEditCtrl extends MobxState implements ICollectionViewController {
	@observable.ref changeState: CollectionChangeState | null = null;
	isFormOpen: ObservableMap<string, boolean> = observable.map<string, boolean>({}, { deep: false });
	isFieldDetailShown: ObservableMap<string, boolean> = observable.map<string, boolean>({}, { deep: false });

	isCollectionLoading = false;
	isCollectionSelected = true;

	@observable isAllExpanded: boolean = true;
	@observable isInEditMode: boolean = false;
	@observable isSaving: boolean = false;

	@observable isMetadataShown: boolean = false;
	@observable isZoomShown: boolean = true;
	@observable isPhotomeasure: boolean = false;
	@observable isTopologicalEditor: boolean = true;
	@observable isCarouselShown: boolean = false;
	allowedTypes = [];
	@observable isInUploadMode = false;
	@observable fieldsInLoadingState: string[] = [];

	@observable.shallow file: FileUploadReader = null;
	@observable.shallow image: ImageUploadReader = null;

	readonly kind = 'TopologicalEditorCollectionEditCtrl';

	static is(ctrl: ICollectionViewController): ctrl is TopologicalEditorCollectionEditCtrl {
		return ctrl.kind === 'TopologicalEditorCollectionEditCtrl';
	}

	public collectionEditState: CollectionEditState;
	private parentState: TopologicalEditorController;

	constructor(collection: CollectionModel, parentState: TopologicalEditorController) {
		super();

		makeObservable(this);

		this.collectionEditState = new CollectionEditState(collection);
		this.changeState = new CollectionChangeState();
		this.parentState = parentState;
		this.isCarouselShown = this.hasPhotos;

		this.reaction((_) => this.file && this.file.buffer, this.processFile);
		this.reaction((_) => this.image && this.image.buffer, this.processImage);
	}

	get collection() {
		return this.collectionEditState.collection;
	}

	get revision() {
		return this.collection.revision;
	}

	get id() {
		return this.collection.id;
	}

	get isEditing() {
		return this.changeState != null && (this.isInEditMode || this.isPhotomeasure || this.isTopologicalEditor);
	}

	get isValid() {
		return this.changeState != null && this.changeState.isValid && !this.isInUploadMode;
	}

	@computed
	get isMeasurementActive(): boolean {
		return this.parentState.measurementState.isActive && !this.parentState.measurementState.isPaused;
	}

	@computed
	get isRelocatePoleActive(): boolean {
		return this.parentState.isRelocatePoleActive;
	}

	get isEditable() {
		return Services.Token.has(IkeToken.FEATURE_MAP_MODE);
	}

	get isUploading() {
		return this.changeState != null && this.isInUploadMode;
	}

	@action.bound zoomToCollection() {
		//TODO: add zoom to collection
	}

	@action clearOpenState() {
		this.isFormOpen.clear();
		this.isFieldDetailShown.clear();
	}

	@action.bound async abortChanges(e: Event) {
		e.preventDefault();

		let shouldDiscard = false;
		try {
			shouldDiscard = await this.checkAndDiscardChanges();
		} catch (error) {
			Services.Log.Logger.error(error, 'TE: Discard changes failed');
			NotificationService.error({
				key: T.ERROR_GENERAL,
			});
		}

		if (shouldDiscard && this.changeState != null) {
			this.discardAndDestroyEdit();
		}

		return false;
	}

	@action.bound selectAnchorField(e: MouseEvent, field: CollectionModelFieldAnchor) {
		e.preventDefault();
		Services.Log.Logger.warn(
			`1. Anchor is edit by user ${Services.User.current.email.toString()} in department ${Services.Department.current.name.toString()} of organization ${Services.Department.currentOrg.name.toString()}`
		);
	}

	async checkAndDiscardChanges(): Promise<boolean> {
		const response = await ModalService.open({
			title: T.DISCARD_CHANGES_TITLE,
			content: Translate(T.DISCARD_CHANGES_CONFIRM),
			okName: T.DISCARD_CHANGES_DISCARD,
			hasCancel: true,
		});
		return response.ok;
	}

	@action public discardAndDestroyEdit() {
		if (this.changeState != null) {
			//discard everything
			while (this.collectionEditState.isDirty) {
				this.collectionEditState.reset();
			}

			// Clean up modified targets originating from this pole.
			// In the topo editor, each pole's edit state is managed independently —
			// discarding Pole A should not affect targets from Pole B.
			if (CurrentState.CollectionLink.hasModifiedTargets) {
				CurrentState.CollectionLink.cleanupTargetsForSource(this.collection.id);
			}
		}
		this.changeState = new CollectionChangeState();
		this.isInEditMode = false;

		TopologicalPointCollectionCache.dropCollectionLocations(this.revision);
		TopologicalPointCollectionCache.flushVectors();

		// If this was a newly created pole, remove it from the list and deselect
		const newPoleIndex = this.parentState.newPoleModel.findIndex((pole) => pole.revision === this.revision);
		if (newPoleIndex !== -1) {
			this.parentState.newPoleModel.splice(newPoleIndex, 1);
			this.parentState.removeEditController(this.revision);
			this.parentState.setSelectedCollectionRevision(null);
			this.parentState.refreshOverlay();
		}
	}

	@action.bound async saveChanges(e: Event) {
		e.preventDefault();

		const changes = this.changeState;
		if (!changes || !this.changeState.isValid) {
			return;
		}

		if (!this.collectionEditState.isDirty) {
			return;
		}

		if (this.isSaving) {
			return;
		}

		const originalCollection = this.collection;
		const originalRevision = originalCollection.revision;
		const draw = this.collectionEditState.currentDraw;
		const drawState = draw != null ? draw.state : null;
		const wasNewPole = this.parentState.newPoleModel.some((pole) => pole.revision === originalRevision);

		this.setSaving(true);

		try {
			// Services.Collection.save() handles new collection summary creation, refreshing, and fetching
			const result = await Services.Collection.save(originalCollection);

			// Remove from newPoleModel if it was a newly created pole
			if (wasNewPole) {
				runInAction(() => {
					const index = this.parentState.newPoleModel.findIndex((pole) => pole.revision === originalRevision);
					if (index !== -1) {
						this.parentState.newPoleModel.splice(index, 1);
					}
					this.parentState.removeEditController(originalRevision);
				});
			}

			await this.collectionCommitted(result, originalCollection, drawState);
			this.collectionEditState.reset();

			this.parentState.refreshOverlay();

			// Update selection to the new revision after everything is ready
			if (wasNewPole) {
				this.parentState.setSelectedCollectionRevision(result.revision);
			}
		} catch (error: any) {
			this.setSaving(false);

			Services.Log.Logger.error(error);

			NotificationService.error({
				key: T.COLLECTION_EDIT_SAVE_ERROR,
			});
		}
	}

	// This is a specific method for saving a collection as part of a batch edit
	// inside of a "job". It provides minimal error handling and state modification
	// with the expectation that error management and logging will be handled upstream.
	@action.bound public async batchSaveChanges() {
		const changes = this.changeState;

		if (!changes || !this.changeState.isValid || !this.collectionEditState.isDirty || this.isSaving) {
			return null;
		}

		const originalCollection = this.collection;
		const originalRevision = originalCollection.revision;
		const wasNewPole = this.parentState.newPoleModel.some((pole) => pole.revision === originalRevision);

		this.setSaving();

		try {
			// Services.Collection.save() handles new collection summary creation, refreshing, and fetching
			const result = await Services.Collection.save(originalCollection);

			this.setSaving(false);
			this.collectionEditState.reset();

			TopologicalPointCollectionCache.dropCollectionVectors(originalRevision);

			// Remove from newPoleModel if it was a newly created pole
			if (wasNewPole) {
				runInAction(() => {
					const index = this.parentState.newPoleModel.findIndex((pole) => pole.revision === originalRevision);
					if (index !== -1) {
						this.parentState.newPoleModel.splice(index, 1);
					}
				});
			}

			return result;
		} finally {
			this.setSaving(false);
		}
	}

	@action setSaving(value: boolean = true) {
		this.isSaving = value;
	}

	private async collectionCommitted(
		collection: CollectionJSONObject,
		originalCollection: CollectionModel,
		drawState: IkeDrawState
	) {
		const { TimingEventType, TimingEventAction } = Services.Analytics;
		Services.Analytics.trackTiming(
			TimingEventType.TOPOLOGICAL_EDITOR,
			TimingEventAction.ENTER,
			originalCollection.id,
			new Date(this.changeState.startTime)
		);
		Services.Analytics.trackTiming(
			TimingEventType.TOPOLOGICAL_EDITOR,
			TimingEventAction.EXIT,
			originalCollection.id,
			new Date()
		);

		this.changeState.startTime = Date.now();

		runInAction(() => {
			// PM page will have an IkeDraw. The map page will not.
			if (this.collectionEditState.currentDraw != null) {
				this.collectionEditState.currentDraw.state.setFromState(drawState);
			}
			this.setSaving(false);
		});
	}

	@action toggleFieldDetail(fieldId: string) {
		if (this.isFieldDetailShown.get(fieldId)) {
			this.isFieldDetailShown.delete(fieldId);
		} else {
			this.isFieldDetailShown.set(fieldId, true);
		}
	}

	@action async clearModalField(field: CollectionModelField) {
		const response = await ModalService.open({
			title: T.FILE_DELETE_TITLE,
			content: Translate(T.FILE_DELETE_MESSAGE, field.value),
			okName: T.FILE_DELETE,
			hasCancel: true,
		});
		if (response.ok) {
			field.Mutator.clear();

			const state = this.changeState.getState(field.id);
			if (state != null) {
				state.setValue(null);
			}
		}
	}

	@action clearField(field: CollectionModelField) {
		field.Mutator.clear();

		if (CollectionModelFieldVector.isVectorField(field)) {
			TopologicalPointCollectionCache.dropCollectionVectors(this.revision);
		}

		const state = this.changeState.getState(field.id);
		if (state != null) {
			state.setValue(null);
		}
	}

	@action.bound toggleCarousel() {
		this.isCarouselShown = !this.isCarouselShown;
	}

	@computed get hasPhotos(): boolean {
		if (this.collection == null) {
			return false;
		}
		let found = false;
		this.collection.forEachField((field) => {
			if (CollectionModelFieldTrueSize.isTrueSizeField(field) && field.getCaptures().length > 0) {
				found = true;
				return false;
			}
			return true;
		});
		return found;
	}

	/**
	 * File upload related functions
	 */

	@action.bound onDragOver(e: any, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const dragArea = document.getElementById(`drag-area-${field.id}`);
		if (dragArea == null) {
			return;
		}

		dragArea.style.backgroundColor = DefaultTheme.color.accent;
	}

	@action.bound onDragLeave(e: any, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const dragArea = document.getElementById(`drag-area-${field.id}`);
		if (dragArea == null) {
			return;
		}

		dragArea.style.backgroundColor = null;
	}

	@action.bound onDragEnd(e: any, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const dragArea = document.getElementById(`drag-area-${field.id}`);
		if (dragArea == null) {
			return;
		}

		dragArea.style.backgroundColor = null;
	}

	@action.bound openImageDialog(e: MouseEvent, field: CollectionModelFieldImage) {
		e.preventDefault();

		const imageInput = document.getElementById(field.id);
		if (imageInput == null) {
			throw new Error('Unable to find file input: "' + field.id + "'");
		}

		var evt = document.createEvent('MouseEvents');
		evt.initEvent('click', true, false);
		imageInput.dispatchEvent(evt);
	}

	@action.bound openFileDialog(e: MouseEvent, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const fileInput = document.getElementById(field.id);
		if (fileInput == null) {
			throw new Error('Unable to find file input: "' + field.id + "'");
		}

		var evt = document.createEvent('MouseEvents');
		evt.initEvent('click', true, false);
		fileInput.dispatchEvent(evt);
	}

	@action.bound onImage(e: Event, field: CollectionModelFieldImage) {
		const imageInput = e.target as HTMLInputElement;
		if (imageInput == null) {
			throw new Error(`Unable to find file input: "${field.id}"`);
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'image:click', 'button', 1);
		this.loadImages(imageInput.files, field);
	}

	@action.bound onFile(e: Event, field: CollectionModelFieldFileTool) {
		const fileInput = e.target as HTMLInputElement;
		if (fileInput == null) {
			throw new Error('Unable to find file input: "' + field.id + "'");
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:click', 'button', 1);
		this.loadFiles(fileInput.files, field);
	}

	@action.bound update(e: DragEvent, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		if (e.dataTransfer == null) {
			return;
		}

		const files = e.dataTransfer.files;

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:drop', 'drop', 1);
		this.loadFiles(files, field);
	}

	loadImages(images: FileList, field: CollectionModelFieldImage) {
		this.image = null;

		if (images && images[0]) {
			const image = images[0];
			const validationError = validateImageFile(image);
			if (validationError) {
				NotificationService.error({ key: T[validationError], attrs: { limit: `${MAX_UPLOAD_FILE_SIZE_MB} MB` } });
				this.unsetLoading(field.id);
				return;
			}

			this.image = new ImageUploadReader(image, field);
		}
	}

	loadFiles(files: FileList, field: CollectionModelFieldFileTool) {
		this.file = null;

		if (files == null) {
			return;
		}

		if (files.length > 1) {
			Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:count', String(files.length), 1);
			NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
			this.unsetLoading(field.id);
			return;
		}

		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			if (MAX_UPLOAD_FILE_SIZE_BYTE < file.size) {
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:large', 'large', file.size);
				NotificationService.error({ key: T.FILE_TOO_LARGE, attrs: { limit: `${MAX_UPLOAD_FILE_SIZE_MB} MB` } });
				this.unsetLoading(field.id);
				continue;
			}

			const fileType = file.type.toLowerCase();
			if (this.allowedTypes.length > 0 && fileType !== '' && this.allowedTypes.indexOf(fileType) === -1) {
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:type', fileType, 1);
				NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
				this.unsetLoading(field.id);
				continue;
			}

			this.file = new FileUploadReader(file, field);
		}
	}

	@action.bound async processFile() {
		if (this.file?.buffer) {
			this.setLoading(this.file.field.id);

			const rest = Services.Department.rest;
			try {
				const attachment = await rest.Attachment.set(this.file.name, this.file.buffer, this.file.type);

				let metadata = {};
				try {
					metadata = await rest.Attachment.parse(attachment.objectId);
				} catch (error) {
					Services.Log.Logger.info(`File cannot be parsed: ${error}`);
				}

				this.file.field.Mutator.manual({
					filename: this.file.name,
					attachmentId: attachment.objectId,
					size: attachment.size,
					metadata,
				});
			} catch (_e) {
				NotificationService.error({ key: T.ERROR_GENERAL });
			}

			this.unsetLoading(this.file.field.id);
		}
	}

	@action.bound async processImage() {
		if (this.image?.buffer) {
			this.setLoading(this.image.field.id);

			const rest = Services.Department.rest;
			let capture: CaptureFileJSONObject = null;
			try {
				capture = await rest.Capture.Image.image(IkeId.v4(), this.image.buffer, 1, new Date());

				this.image.field.Mutator.add(capture.objectId);
			} catch (e) {
				NotificationService.error({ key: T.ERROR_GENERAL });
				Services.Log.Logger.info(`Image cannot be parsed: ${e}`);
			}

			this.unsetLoading(this.image.field.id);
		}
	}

	@action async fileDownload(field: CollectionModelFieldFileTool) {
		if (field.attachmentId) {
			this.setLoading(field.id);

			const token = `Bearer ${Services.SSO.token}`;

			const url = '/v1/file/' + field.attachmentId + '?departmentId=' + Services.Department.current.objectId;

			const options = {
				headers: {
					Authorization: token,
				},
			};
			fetch(url, options)
				.then((res) => {
					if (!res.ok) {
						Services.Log.Logger.error('File possibly infected');
						NotificationService.error({
							key: T.ERROR_DOWNLOAD_FAIL,
						});
						return null;
					}
					return res.blob();
				})
				.then((blob) => {
					if (blob != null) {
						var file = window.URL.createObjectURL(blob);
						var link = Services.Window.document.createElement('a');
						link.download = field.value;
						link.href = file;
						Services.Window.document.body.appendChild(link);
						link.click();
						link.remove();
					}
					this.unsetLoading(field.id);
				})
				.catch((e) => {
					Services.Log.Logger.error(e, 'File download error');
					NotificationService.error({
						key: T.ERROR_GENERAL,
					});
					this.unsetLoading(field.id);
				});
		}

		return false;
	}

	setLoading(fieldId) {
		this.isInUploadMode = true;
		this.fieldsInLoadingState.push(fieldId);
	}

	unsetLoading(fieldId) {
		this.isInUploadMode = false;
		this.fieldsInLoadingState.splice(this.fieldsInLoadingState.indexOf(fieldId), 1);
	}

	/**
	 * File upload related functions end
	 */

	fillAddressDisabled(field: CollectionModelField): boolean {
		if (field.value) {
			return true;
		}

		if (this.locationForField(field) == null) {
			return true;
		}

		if (!this.isEditing || this.isSaving) {
			return true;
		}

		return false;
	}

	geocodingButtonTitle(field: CollectionModelField): string {
		if (field.value) {
			return 'Already has value';
		}

		if (this.locationForField(field) == null) {
			return 'There is no location';
		}

		return 'Get address';
	}

	location(): GeoPointJSON | null {
		const revision = this.collection.revision;
		const collectionSummary = AppStore.CollectionSummary.get(revision);
		if (collectionSummary == null) {
			return null;
		}
		const location = collectionSummary.location;

		if (!(location && location.coordinates)) {
			return null;
		}

		return location;
	}

	/**
	 * For an address-lookup field nested in a subform, prefer the location field within
	 * the same subform instance. For top-level address fields, use the persisted collection
	 * summary location.
	 */
	locationForField(field: CollectionModelField): GeoPointJSON | null {
		if (field.parent.parent != null) {
			const subFormLocation = field.parent.location;
			if (subFormLocation) {
				return ParseJSON.toGeoPoint(subFormLocation);
			}
		}
		return this.location();
	}

	@action async fillAddress(field: CollectionModelField) {
		const location = this.locationForField(field);
		if (location == null) {
			return;
		}

		const addressFields = await Services.Geocoder.getAddress(location);
		const address = addressFields.address;
		const fieldState = this.changeState.getState(field.id);
		// Sync field.value before inputState — viewEdit re-syncs inputState ← field.value each
		// render, which can race with setValue's onValue reaction and reset the new value.
		if (CollectionModelFieldText.isTextField(field)) {
			field.Mutator.manual(address);
		}
		fieldState.setValue(address);
		if (address === Translate(T.ERROR_GENERAL)) {
			fieldState.setError(address);
		}

		const metadata = field.collection.schema.metadata;
		const city = addressFields.city;
		const state = addressFields.state;

		if (city && metadata.options.address_lookup.city) {
			const cityFieldId = metadata.options.address_lookup.city.field;
			const cityFieldState = this.changeState.getState(CollectionModelField.getStateId(field.parent, cityFieldId));
			if (cityFieldState) {
				const cityField = field.parent.getOptionalField<CollectionModelField>(cityFieldId);
				if (cityField && CollectionModelFieldText.isTextField(cityField)) {
					cityField.Mutator.manual(city);
				}
				cityFieldState.setValue(city);
			}
		}

		if (state && metadata.options.address_lookup.state) {
			const stateFieldId = metadata.options.address_lookup.state.field;
			const stateFieldState = this.changeState.getState(CollectionModelField.getStateId(field.parent, stateFieldId));
			if (stateFieldState) {
				const stateField = field.parent.getOptionalField<CollectionModelField>(stateFieldId);
				if (stateField && CollectionModelFieldText.isTextField(stateField)) {
					stateField.Mutator.manual(state);
				}
				stateFieldState.setValue(state);
			}
		}
	}

	@action clearError(field: CollectionModelField) {
		const fieldState = this.changeState.getState(field.id);
		fieldState.setValue('');
		fieldState.setError('');
	}

	clearErrorDisabled(field: CollectionModelField): boolean {
		if (!this.isEditing || this.isSaving) {
			return true;
		}

		const state = this.changeState.getState(field.id);
		if (state.hasError() === true) {
			return false;
		}

		return true;
	}

	isExpanded(instanceId: string): boolean {
		if (this.isAllExpanded) {
			return this.isFormOpen.get(instanceId) !== false;
		}

		return this.isFormOpen.get(instanceId);
	}

	@action toggleExpanded(e: MouseEvent, instanceId: string) {
		e.preventDefault();
		let prev = this.isFormOpen.get(instanceId);
		if (prev == null) {
			prev = this.isAllExpanded;
		}
		this.isFormOpen.set(instanceId, !prev);
		return false;
	}

	@action setZoomWindow(show: boolean) {
		this.isZoomShown = show;
	}

	@action.bound toggleMetaInfo() {
		this.isMetadataShown = !this.isMetadataShown;
		this.isZoomShown = false;
	}

	@action.bound toggleZoomWindow() {
		this.isZoomShown = !this.isZoomShown;
		this.isMetadataShown = false;
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Collection,
			`zoom:${this.isZoomShown ? 'show' : 'hide'}`,
			this.collection.revision
		);
	}

	@action.bound toggleExpandAll() {
		this.isAllExpanded = !this.isAllExpanded;
		this.isFormOpen.clear();
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Collection,
			`form:${this.isAllExpanded ? 'expand' : 'collapse'}`,
			this.collection.revision
		);
	}

	@action.bound enterEditMode() {
		const collection = this.collection;
		if (collection != null) {
			Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'Collection:edit', collection.uuid, 1);
		}
		this.isInEditMode = true;
	}

	@action exitEditMode() {
		if (this.collectionEditState.isDirty) {
			throw new Error('Must discard changes before exiting collection edit mode');
		}

		this.isInEditMode = false;
	}

	async beginSpanLink(c: CollectionModelBase) {
		const collection = c.collection;
		const state = new SpanLinkChooserState(collection);

		const result = await ModalService.open({
			title: MultiListChooserTitle.view(state),
			content: () => MultiListChooserContent.view(state),
			className: MultiListChooserCSS.modal,
			hasCancel: true,
		});

		if (!result.ok) {
			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.Mutate,
				'span:link:cancel',
				this.collection.revision
			);
			return;
		}

		if (state.value == null) {
			state.selectDefaultValue();
		}

		const spanLinkRecord = state.getSelectedSpanLinkRecord();
		if (spanLinkRecord == null) {
			return;
		}

		runInAction(() => {
			try {
				const newSubformInstance = Services.SubFormLink.copySubForm(collection, spanLinkRecord);

				// Invalidate vector cache so the new/updated span renders with correct position
				TopologicalPointCollectionCache.dropCollectionVectors(collection.revision);

				NotificationService.success({
					key: T.SPAN_LINK_SUCCESS,
					attrs: {
						toSpan: newSubformInstance.title,
						fromCollection: spanLinkRecord.collection.title,
					},
				});

				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'span:link', this.collection.revision);
			} catch (e) {
				Services.Log.Logger.error(e, 'Span link failed');
				NotificationService.error({
					key: T.ERROR_GENERAL,
				});
			}
		});
	}

	@computed get matches(): FieldJoinMatch[] {
		if (!Services.Measurement.isFormSupported(this.collection.schema)) {
			return [];
		}

		return Services.Measurement.getFieldJoins(this.collection);
	}

	getJoin(field: CollectionModelField): FieldJoin | null {
		for (const match of this.matches) {
			const join = match.getJoin(field.parent.instanceId, field.schema.id);
			if (join != null) {
				return join;
			}
		}

		return null;
	}

	getOppositeJoin(field: CollectionModelField): FieldJoin | null {
		for (const match of this.matches) {
			const join = match.getOppositeJoin(field.parent.instanceId, field.schema.id);
			if (join != null) {
				return join;
			}
		}

		return null;
	}

	async chooseLinkingCollection(field: CollectionModelField): Promise<CollectionModel | null> {
		if (this.parentState.newPoleModel.find((pole) => pole.revision === this.collection?.revision)) {
			NotificationService.error({
				key: T.TOPO_MUST_SAVE_FIRST,
			});
			return null;
		}

		CurrentState.CollectionLink.beginLinking(field.id);

		return new Promise<CollectionModel | null>((resolve) => {
			const disposer = reaction(
				() => CurrentState.CollectionLink.isLinking,
				() => {
					disposer();

					const revision = CurrentState.CollectionLink.collectionRevision;
					if (revision == null) {
						return resolve(null);
					}

					const collectionModel = AppStore.Collection.get(revision);

					if (collectionModel == null) {
						NotificationService.error({
							key: T.TOPO_MUST_SAVE_TARGET_FIRST,
						});
						return resolve(null);
					}
					return resolve(collectionModel);
				}
			);
		});
	}

	canInspectCollection(summary: CollectionSummaryJSONObject): boolean {
		if (summary == null) {
			return false;
		}

		const tag = AppStore.Tag.get(summary.tags[0]);
		return Services.Search.includesTag(tag);
	}

	inspectCollection(revision: string) {
		this.parentState.setSelectedCollectionRevision(revision);
	}

	public redrawMap() {
		this.parentState.map.overlay.resetMoveDelta();
	}

	/**
	 * Register a collection for editing in the topological editor.
	 * Creates an edit controller with proper proxy state if one doesn't exist.
	 * Follows the same pattern as bulkSnap().
	 */
	public registerCollectionForEditing(collection: CollectionModel): void {
		if (!this.parentState.getEditCtrlByRevision(collection.revision)) {
			this.parentState.createEditControllerForCollection(collection);
			this.parentState.refreshOverlay();
		}
	}

	/**
	 * Revert all changes on a registered collection's edit controller.
	 * Used when discarding a two-way span link or on link error.
	 */
	public revertRegisteredCollection(collectionRevision: string): void {
		const ctrl = this.parentState.getEditCtrlByRevision(collectionRevision);
		if (ctrl) {
			while (ctrl.collectionEditState.isDirty) {
				ctrl.collectionEditState.reset();
			}
		}
	}

	get useColorCoding() {
		return false;
	}
}
